import React, { useEffect } from 'react';
import { TextField } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { FieldWrapper } from '../mini_components';
import { computeImc, findWeightHeight } from './imc';

/**
 * SHER-210 — read-only IMC (BMI) field that auto-calculates from the sibling
 * weight and height fields already present on the form (identified by name
 * convention). Avoids duplicating weight/height inputs.
 */
interface ImcFieldProps {
    formValues?: Record<string, { label?: any; value?: any }>;
    input: { value?: any; onChange: (value: any) => void };
    label?: React.ReactNode;
    fullWidth?: boolean;
}

function ImcField({ formValues, input, label, fullWidth }: ImcFieldProps) {
    const { weight, height } = findWeightHeight(formValues);
    const imc = computeImc(weight, height);

    // Keep the redux-form value in sync so the computed IMC is persisted as a
    // normal record value. Guarded against the no-op case to avoid update loops.
    useEffect(() => {
        const next = imc === null ? '' : String(imc);
        const current = input.value == null ? '' : String(input.value);
        if (current !== next) {
            input.onChange(next);
        }
    }, [imc, input.value]);

    return (
        <FieldWrapper noWrap={fullWidth}>
            <TextField
                label={label}
                value={imc === null ? '' : imc.toFixed(2)}
                InputProps={{ readOnly: true }}
                variant="outlined"
                margin="dense"
                fullWidth
                helperText={<Translate id="hospital.imc-calculated-helper" />}
            />
        </FieldWrapper>
    );
}

export default ImcField;
